import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Slider } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { getMarks } from '@/components/CapacitySlider';
import { maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { ensureSystemDiskSizeOnForm } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';

import styles from './system-disk-setting.module.less';

const FormItem = Form.Item;

interface SystemDiskSettingProps {
  form: FormInstance;
  /** 表单字段，如 master.systemDiskSize / worker.systemDiskSize */
  field: string;
  /** 节点角色：Master / Worker */
  role?: 'Master' | 'Worker';
  min?: number;
  max?: number;
  validateTrigger?: string[];
}

/** 系统盘大小：仅维护 UI 字段，提交时再组装 resource.storage */
export function SystemDiskSetting({ form, max = 100, min = 10, field, role = 'Worker', validateTrigger = [] }: SystemDiskSettingProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const label = role ? `${t('common:system.disk.size')}（${role}）` : t('common:system.disk.size');

  // Slider 在值为 undefined 时会显示 min，但 required 校验读的是 store，需显式写入默认值
  useEffect(() => {
    if (sectionReadOnly) return;
    ensureSystemDiskSizeOnForm(form, field, min);
  }, [form, field, min, sectionReadOnly]);

  return (
    <div className={styles.systemDiskRow}>
      <FormItem
        className={styles.systemDiskFormItem}
        label={label}
        field={field}
        required
        validateTrigger={validateTrigger}
        rules={maasRequiredRules(label)}
      >
        <Slider
          disabled={sectionReadOnly}
          showInput={{
            hideControl: false,
            readOnly: sectionReadOnly,
            style: { width: 80, marginLeft: 10 },
          }}
          marks={getMarks(min, max)}
          max={max}
          min={min}
        />
      </FormItem>
      <div className={styles.systemDiskUnit}>Gi</div>
    </div>
  );
}

export default SystemDiskSetting;
